import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, TablePagination, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import type {
  Booking,
  FrontendStatus,
  PaymentStatus,
} from "../../../types/bookings";

import type {
  BookingResponse,
  BookingsByTypeProps,
  BackendStatus,
} from "../types/list-bookings";
import { RootState } from "../../../store/Index";
import apiService, { BaseURL } from "../../../service/Api";
import { checkPermission } from "../../../service/permissionGuard";
import { PERMISSIONS } from "../../../constants/permissions";
import formatDate from "../../../utils/FormatDate";
import { logout } from "../../../store/slice/AuthSlice";
import StatsSection from "./StatsSection";
import StreamListHeader from "./StreamListHeader";
import StreamListTable from "./StreamListTable";
import WeekCalendar from "./WeekCalendar";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import RequestStatusChangeModal from "../view-booking2/components/RequestStatusChangeModal";
import { createStatusChangeRequest } from "../view-booking2/components/statusChangeRequestStorage";

const statusMapToFrontend: Record<BackendStatus, FrontendStatus> = {
  pending: "enquiry",
  confirmed: "confirmed",
  completed: "fulfilled",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

type BookingType =
  | "CHURCH"
  | "FUNERAL"
  | "FUNERAL_HOME"
  | "OTHER"
  | "CONFERENCE"
  | "EVENT";

const BookingListVersion2: React.FC<BookingsByTypeProps> = ({
  bookingType,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = currentUser?.role === "admin";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setError] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [pendingConfirmId, setPendingConfirmId] = useState<number | null>(
    null,
  );

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const apiPage = page + 1;
      const params: Record<string, string | number> = {
        page: apiPage,
        page_size: rowsPerPage,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      if (bookingType) {
        params.booking_type = bookingType;
      }

      const response = await apiService.get<{
        count: number;
        results: BookingResponse[];
      }>("/api/bookings/list-bookings/", params);

      const fetchedBookings: Booking[] = response.data.results
        .filter((booking): booking is BookingResponse => {
          if (!booking || typeof booking !== "object" || !booking.id) {
            console.warn("Invalid booking data filtered out:", booking);
            return false;
          }
          return true;
        })
        .map((booking) => {
          let computedTitle: string;

          if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CHURCH" &&
            booking.services &&
            booking.services.length > 0 &&
            booking.services[0].church_name
          ) {
            computedTitle = booking?.title || "";
          } else if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CONFERENCE" &&
            booking.events.length > 0
          ) {
            computedTitle = booking.events[0].title || "";
          } else if (booking.deceased && booking.deceased.length > 0) {
            const firstDeceased = booking.deceased[0];
            const deceasedName = firstDeceased.first_name || "";
            const deceasedSurname = firstDeceased.surname || "";
            computedTitle =
              `${deceasedName} ${deceasedSurname}`.trim() ||
              "Deceased Name N/A";
          } else if (
            booking.client &&
            (booking.client.salutation || booking.client.company_name)
          ) {
            computedTitle = `${booking.client.salutation || ""} ${
              booking.client.company_name || ""
            }`.trim();
          } else if (booking.title) {
            computedTitle = booking.title;
          } else {
            computedTitle = "Booking N/A";
          }

          const service =
            booking.service && booking.service.length < 50
              ? booking.service
              : booking.booking_type && booking.booking_type.length < 50
                ? booking.booking_type
                : "N/A";

          const packageName =
            booking.package_item?.name || "No selected package";

          const paymentStatus: PaymentStatus =
            booking.payment_status?.toLowerCase() === "paid"
              ? "paid"
              : booking.payment_status?.toLowerCase() === "sponsored"
                ? "sponsored"
                : booking.payment_status?.toLowerCase() === "other"
                  ? "other"
                  : "pending";

          const startTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].start_time || "00:00:00"
              : "00:00:00";

          const endTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].end_time || "23:59:59"
              : "23:59:59";

          const validStatuses = [
            "pending",
            "confirmed",
            "completed",
            "cancelled",
            "lost",
            "testing",
          ];

          const status: FrontendStatus = validStatuses.includes(booking.status)
            ? statusMapToFrontend[booking.status as BackendStatus] || "enquiry"
            : "enquiry";

          const bookingTypeValue =
            booking.booking_type && booking.booking_type.length < 50
              ? booking.booking_type
              : "N/A";

          const dateOfService = formatDate(
            booking?.services[0]?.date_of_service ||
              `${booking?.events[0]?.start_date_of_event || ""} -${
                booking?.events[0]?.end_date_of_event || ""
              }` ||
              "No date of service",
          );

          const branch = booking.contact?.company_branch;
          const companyBranch = branch
            ? [branch.company?.name, branch.name].filter(Boolean).join(" - ")
            : booking.funeral_home?.name || null;

          return {
            id: booking.id,
            title: computedTitle,
            service,
            package: packageName,
            paymentStatus,
            dateOfService: dateOfService,
            status,
            bookingType: bookingTypeValue,
            startTime,
            endTime,
            companyBranch,
          };
        });

      setBookings(fetchedBookings);
      setRowCount(response.data.count);
      setError(null);
    } catch (err: unknown) {
      let errorMessage = "Failed to load bookings. Please try again.";
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { detail?: string } };
          message?: string;
          stack?: string;
        }
      )?.response;
      if (errorResponse?.status === 500) {
        errorMessage = "Server error: Unable to retrieve bookings.";
      } else if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  }, [appliedSearchTerm, bookingType, dispatch, navigate, page, rowsPerPage]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (inputValue !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(inputValue);
        setPage(0);
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, appliedSearchTerm]);

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setAppliedSearchTerm(inputValue);
      setPage(0);
    }
  };

  const handleSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setAppliedSearchTerm(inputValue);
    setPage(0);
  };

  const handleAddBooking = () => {
    const serviceType = bookingType || "FUNERAL";
    const pathSlug = serviceType.toLowerCase().replace("_", "-");

    if (BaseURL === "https://staging.muzukuru.com/") {
      navigate(`/bookings/add-v2/${pathSlug}`, {
        state: { serviceType },
      });
      return;
    }

    navigate(`/bookings/add/${pathSlug}`, {
      state: {
        serviceType,
        defaultBookingType: serviceType,
      },
    });
  };

  // const handleAddBooking = () => {
  //   const serviceType = bookingType || "FUNERAL";
  //   const pathSlug = serviceType.toLowerCase().replace("_", "-");

  //   navigate(`/bookings/add-v2/${pathSlug}`, {
  //     state: { serviceType },
  //   });
  // };

  const handleRowClick = (id: number) => {
    navigate(`/bookings/${id}`);
  };

  const handleEdit = (id: number) => {
    if (BaseURL === "https://staging.muzukuru.com/") {
      navigate(`/bookings/${id}/edit-v2`);
      return;
    }
    navigate(`/bookings/${id}/edit`);
  };

  const handlePaymentStatusChange = async (
    id: number,
    newStatus: PaymentStatus,
  ) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, paymentStatus: newStatus } : b)),
    );

    try {
      await apiService.patch(`/api/bookings/booking/${id}/`, {
        payment_status: newStatus,
      });

      toast.success("Payment status updated successfully");
    } catch {
      toast.error("Failed to update payment status");
      fetchBookings();
    }
  };

  const handleMemorialPageClick = (id: number) => {
    const selectedBooking = bookings.find((booking) => booking.id === id);

    if (!selectedBooking) return;

    const streamTitle = selectedBooking.title || "";
    const prefix = "Stream for ";
    const nameForSlug = streamTitle.startsWith(prefix)
      ? streamTitle.slice(prefix.length)
      : streamTitle;

    const slug = encodeURIComponent(nameForSlug.trim().replace(/\s+/g, "-"));

    const service = (selectedBooking.service || "").toLowerCase();

    if (service === "conference") {
      navigate(`/conference-page/${id}/${slug || "conference"}`);
      return;
    }

    if (service === "church") {
      navigate(`/church-page/${id}/${slug || "church"}`);
      return;
    }

    navigate(`/memorial-page/${id}/${slug || "memorial"}`);
  };

  const confirmDelete = (id: number) => {
    setBookingToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (bookingToDelete === null) return;
    try {
      checkPermission(PERMISSIONS.BOOKING_DELETE);
      await apiService.delete(`/api/bookings/booking/${bookingToDelete}/`);
      setBookings((prevBookings) =>
        prevBookings.filter((booking) => booking.id !== bookingToDelete),
      );
      toast.success("Booking deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
      fetchBookings();
    } catch (err: unknown) {
      setError("Failed to delete booking");
      toast.error("Failed to delete booking", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Delete booking error:", err);
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setBookingToDelete(null);
  };

  const handleSendProposal = (id: number) => {
    navigate("/proposals/proposals-builder", {
      state: { bookingId: id },
    });
  };

  const handleStatusChange = async (id: number, newStatus: FrontendStatus) => {
    if (newStatus === "confirmed" && !isAdmin) {
      setPendingConfirmId(id);
      setStatusChangeModalOpen(true);
      return;
    }

    const backendStatusMap: Record<FrontendStatus, BackendStatus> = {
      enquiry: "pending",
      confirmed: "confirmed",
      fulfilled: "completed",
      cancelled: "cancelled",
      lost: "lost",
      testing: "testing",
    };

    const backendStatus = backendStatusMap[newStatus];

    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status: newStatus } : b)),
    );

    try {
      await apiService.patch(`/api/bookings/booking/${id}/`, {
        status: backendStatus,
      });

      toast.success("Status updated successfully", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
    } catch (err: unknown) {
      console.error(err);

      setError("Failed to update status");
      toast.error("Failed to update status", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      fetchBookings();
    }
  };

  const handleRequestConfirm = async (
    approverIds: number[],
    reason: string,
  ) => {
    if (pendingConfirmId === null) return;
    try {
      await createStatusChangeRequest(
        pendingConfirmId,
        "confirmed",
        approverIds,
        reason,
      );
      toast.success("Approval request sent.", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.reason?.[0] ||
        "Failed to send request. Please try again.";
      toast.error(detail, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCalendarBookingClick = (bookingId: number) => {
    navigate(`/bookings/${bookingId}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 3,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <StatsSection />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: colors.primary[900],
                borderRadius: "12px",
                padding: "20px",
              }}
            >
              <StreamListHeader
                inputValue={inputValue}
                bookingType={(bookingType as BookingType) ?? "FUNERAL"}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSearch={handleSearch}
                onAddBooking={handleAddBooking}
                hasAddPermission={hasPermission("booking.add_booking")}
              />

              <StreamListTable
                bookings={bookings}
                loading={loading}
                onRowClick={handleRowClick}
                onEdit={handleEdit}
                onMemorialPage={handleMemorialPageClick}
                onDelete={confirmDelete}
                onStatusChange={handleStatusChange}
                onSendProposal={handleSendProposal}
                hasEditPermission={hasPermission("booking.change_booking")}
                hasDeletePermission={hasPermission("booking.delete_booking")}
                onPaymentStatusChange={handlePaymentStatusChange}
              />

              {/* Pagination */}
              <TablePagination
                component="div"
                count={rowCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: "1px solid #E0E0E0",
                  "& .MuiTablePagination-toolbar": {
                    justifyContent: "center",
                  },
                }}
              />
            </Paper>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Box sx={{ width: "320px" }}>
            <WeekCalendar onBookingClick={handleCalendarBookingClick} />
          </Box>
        </motion.div>
      </Box>

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
      />

      <RequestStatusChangeModal
        open={statusChangeModalOpen}
        onClose={() => setStatusChangeModalOpen(false)}
        bookingLabel={
          bookings.find((b) => b.id === pendingConfirmId)?.title
        }
        onSubmit={handleRequestConfirm}
      />
    </motion.div>
  );
};

export default BookingListVersion2;
